import type { ToolApprovalCardProps } from "#components/ui/tool-approval/renderers/ToolApprovalRenderer.types.ts";
import type { DraftEditor } from "#features/conversations/components/prepared-action/DraftTextArea.tsx";
import { readString } from "#features/conversations/lib/untypedFields.ts";
import type { ToolApprovalEditError } from "#types/ToolApproval.ts";
import { Avatar, Button, Div, Text } from "@hopper-ui/components";
import { IconLoader } from "@intentui/icons";
import classNames from "classnames";
import { createContext, useContext, useId, type CSSProperties, type ReactNode } from "react";
import "./PreparedActionCard.css";
import "./PreparedActionCard.dark.css";

// How far a draft has gone: open for edits, on its way out, or answered. Only an open draft takes edits.
type DraftMode = "editing" | "sending" | "settled";

interface DraftCardValue {
    mode: DraftMode;
    // The row under the content: the status on the start side, the actions on the end side.
    footer?: ReactNode;
}

const DraftCardContext = createContext<DraftCardValue>({ mode: "editing" });

// What a card puts on the footer's start side, such as an Edit toggle. The footer belongs to the prompt around the card.
const FooterStatusContext = createContext<ReactNode>(null);

// The approval prompt and the answered card tell a card how far the draft has gone, and what its footer holds.
export const DraftCardProvider = DraftCardContext.Provider;

export function useDraftMode(): DraftMode {
    return useContext(DraftCardContext).mode;
}

// Hopper's field label is a 16px heading; the draft cards label a field in 12px medium text, 6px above it.
export function FieldGroup({ label, children }: { label: string; children: (labelId: string) => ReactNode }) {
    const labelId = useId();

    return (
        <div className="wai-pa-field-group">
            <Text id={labelId} size="xs" fontWeight="body-xs-medium" color="neutral">{label}</Text>
            {children(labelId)}
        </div>
    );
}

// The photo when core sends one; the initials without one, and when the photo fails to load.
export function CardAvatar({ name, photoUrl }: { name: string; photoUrl?: string | null }) {
    return (
        <Avatar
            name={name}
            src={photoUrl ?? undefined}
            fallbackSrc={null}
            size="md"
            backgroundColor="neutral-weak"
            color="neutral-weak"
            className="wai-pa-avatar"
            aria-hidden="true"
        />
    );
}

interface DraftSurfaceProps {
    className?: string;
    style?: CSSProperties;
    footerStatus?: ReactNode;
    children: ReactNode;
}

// One frame for every draft card. In the approval prompt, the footer sticks to the conversation's bottom edge.
export function DraftSurface({ className, style, footerStatus, children }: DraftSurfaceProps) {
    const { mode, footer } = useContext(DraftCardContext);

    return (
        <div className={classNames("wai-prepared-action wai-pa-approval", className)} style={style} aria-busy={mode === "sending" || undefined}>
            <div className="wai-pa-content">{children}</div>
            {footer ? <FooterStatusContext.Provider value={footerStatus}>{footer}</FooterStatusContext.Provider> : footerStatus && <DraftFooter status={footerStatus} />}
        </div>
    );
}

interface DraftFooterProps {
    status?: ReactNode;
    actions?: ReactNode;
}

// The status on the start side, the actions on the end side. Without a status of its own, it shows the card's.
export function DraftFooter({ status, actions }: DraftFooterProps) {
    const cardStatus = useContext(FooterStatusContext);

    return (
        <div className="wai-pa-footer">
            {status ?? cardStatus}
            {actions && <div className="wai-pa-actions">{actions}</div>}
        </div>
    );
}

interface DraftActionsProps {
    sendLabel: string;
    discardLabel: string;
    isSending?: boolean;
    onSend: () => void;
    onDiscard: () => void;
}

// Discard stays quiet. Send is a neutral solid in the card's text colour, so it never fights a category tint.
// While the draft is on its way out, Discard leaves and Send spins.
export function DraftActions({ sendLabel, discardLabel, isSending = false, onSend, onDiscard }: DraftActionsProps) {
    return (
        <>
            {!isSending && (
                <Button variant="ghost-secondary" size="xs" className="wai-pa-btn wai-pa-btn-plain" onPress={onDiscard}>
                    <Text>{discardLabel}</Text>
                </Button>
            )}
            <Button variant="primary" size="xs" className="wai-pa-btn wai-pa-btn-solid" isDisabled={isSending} onPress={onSend}>
                {isSending && <IconLoader className="wai-pa-spinner" aria-hidden="true" />}
                <Text>{sendLabel}</Text>
            </Button>
        </>
    );
}

interface QuotedMessageProps {
    author?: string | null;
    // Shown after the author, such as the day the message was written.
    meta?: ReactNode;
    text: ReactNode;
}

// The message a draft answers, above the draft.
export function QuotedMessage({ author, meta, text }: QuotedMessageProps) {
    return (
        <blockquote className="wai-pa-quote">
            <span className="wai-pa-quote-entry">
                {(author || meta) && (
                    <span className="wai-pa-quote-author">
                        {author && <Text size="xs" fontWeight="body-xs-medium" color="neutral">{author}</Text>}
                        {meta}
                    </span>
                )}
                <Text size="sm" color="neutral-weak" className="wai-pa-quote-text">{text}</Text>
            </span>
        </blockquote>
    );
}

// A reply or a message as it went out, on the card's faint fill.
export function SentText({ children }: { children: ReactNode }) {
    return <Text size="sm" color="neutral" className="wai-pa-sent">{children}</Text>;
}

// The edit wins over the arg, an emptied field included, so the field shows what the manager typed.
export function editedText({ args, edits }: ToolApprovalCardProps, key: string): string {
    const edit = edits[key];

    return typeof edit === "string" ? edit : readString(args, key) ?? "";
}

export function textEditor(props: ToolApprovalCardProps, key: string): DraftEditor {
    return { value: editedText(props, key), onChange: (value: string) => props.onEditArgs({ [key]: value }) };
}

export function EditErrors({ errors }: { errors: ToolApprovalEditError[] }) {
    return errors.map(error => (
        <Div key={`${error.field ?? ""}|${error.message}`} role="alert">
            <Text size="xs" color="neutral">{error.message}</Text>
        </Div>
    ));
}
